import React, { FC, useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import ForgotLoginModal from '../../components/Modals/ForgotLoginModal'
import LoginForm from './LoginForm'
import ChangePasswordForm from './ChangePasswordForm'
import EnterMfaForm from './EnterMfaForm'
import HealthSpringLoginSection from './HealthSpringLoginSection'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useFormik } from 'formik'
import { LoginSchema, ResetPasswordSchema } from '../../utils/validation'
import useQuery from '../../hooks/location/useQuery'
import useLoginUser from '../../hooks/authentication/useLoginUser'
import useUserWithRole from '../../hooks/authentication/useUserWithRole'
import useChangeForgotPassword from '../../hooks/authentication/useChangeForgotPassword'
import useValidateMfaToken from '../../hooks/authentication/useValidateMfaToken'
import useAlert from '../../hooks/authentication/useAlert'
import { appConfig } from '../../assets/customizations/config'
import {
  preferedName,
  dataPortalTitle,
  companyDataDescription
} from '../../assets/i18n/i18n'
import { addCompanyPhoneMessage, isDemoTenant } from '../../utils/helpers'
import useValidateForgotPasswordCode from '../../hooks/authentication/useValidateForgotPasswordCode'
import useUserAuthenticated from '../../hooks/authentication/useUserAuthenticated'
import PageTitle from '../../components/PageTitle'
import { useAuthState } from '../../hooks/store/useAuthState'
import useEnvironment from '../../hooks/location/useEnviroment'
import { Alert, Button } from 'react-bootstrap'
import { getApiServer } from '../../utils/domain'
import useForgotLogin from '../../hooks/authentication/useForgotLogin'

const Login: FC = () => {
  const {
    alert: loginAlert,
    handleCloseAlert: handleCloseLoginAlert,
    showAlert: showLoginAlert
  } = useAlert({ show: false, variant: 'danger', message: '' })

  const { data: environment } = useEnvironment()

  const {
    alert: changePassAlert,
    handleCloseAlert: handleCloseChangePassAlert,
    showAlert: showChangePassAlert
  } = useAlert({ show: false, variant: 'danger', message: '' })

  const {
    alert: mfaAlert,
    handleCloseAlert: handleCloseMfaAlert,
    showAlert: showMfaAlert
  } = useAlert({ show: true, variant: 'info', message: 'Please check your email for a validation code' })

  const { sessionTimeOut, setLoginSuccess } = useAuthState()

  const [showMfaForm, setShowMfaForm] = useState(false)
  const [showForgotLogin, setShowForgotLogin] = useState(false)
  const [modalType, setModalType] = useState('')
  const forgotLogin = useForgotLogin()
  const handleOpenForgotLogin = (type: string) => {
    setShowForgotLogin(true)
    setModalType(type)
  }

  const handleHideForgotLogin = () => {
    setShowForgotLogin(false)
  }

  const history = useHistory()
  interface LocationState {
    alertMessage?: string;
  }

  const location = useLocation<LocationState>()
  const query = useQuery()
  const screen = query.get('screen')
  const code = query.get('code')

  const loginUser = useLoginUser()
  const { user, refetch, isLoading: userIsLoading } = useUserWithRole()

  const { remove: removeUserAuthenticatedStatus } = useUserAuthenticated()

  useEffect(() => {
    removeUserAuthenticatedStatus()
  }, [])

  useEffect(() => {
    if (location.state?.alertMessage) {
      showLoginAlert('info', location.state.alertMessage)
      history.replace({ ...location, state: {} })
    }
  }, [location.state])

  const loginFormHandler = useFormik({
    initialValues: {
      username: '',
      password: ''
    },
    validationSchema: LoginSchema,
    onSubmit: (values) => {
      loginUser.mutate(values)
    },
    enableReinitialize: true
  })


  useEffect(() => {
    if (loginUser.isSuccess) {
      showLoginAlert('success', 'Validation code sent...')
      setShowMfaForm(true)
    }
    if (loginUser.isError) {
      showLoginAlert('danger', addCompanyPhoneMessage(loginUser.error?.response.text))
    }

    if (sessionTimeOut) {
      showLoginAlert('info', 'You have been logged out due to inactivity.')
    }
  }, [loginUser.isSuccess, loginUser.isError, sessionTimeOut])

  const validateMfaToken = useValidateMfaToken()

  const mfaFormHandler = useFormik({
    initialValues: {
      mfaCode: '',
    },
    onSubmit: (values) => {
      showMfaAlert('info', 'Checking validation code...')
      validateMfaToken.mutate(values)
    },
    enableReinitialize: true
  })

  useEffect(() => {
    if (validateMfaToken.isSuccess) {
      setLoginSuccess(true)
      refetch()
    }
    if (validateMfaToken.isError) {
      showMfaAlert('danger', validateMfaToken.error?.response.text)
    }
  }, [validateMfaToken.isSuccess, validateMfaToken.isError])

  const changePassword = useChangeForgotPassword()
  const validateForgotPasswordCode = useValidateForgotPasswordCode()

  const changePasswordFormHandler = useFormik({
    initialValues: {
      password: '',
      passwordAgain: ''
    },
    onSubmit: (values) => {
      changePassword.mutate({
        password: values.password,
        resetToken: code,
        userId: validateForgotPasswordCode.data?.id
      })
    },
    validationSchema: ResetPasswordSchema,
    enableReinitialize: true
  })

  useEffect(() => {
    if (changePassword.isSuccess) {
      showLoginAlert('success', 'Successfully changed your password.')
      history.push('/login')
    }
    if (changePassword.isError) {
      showChangePassAlert('danger', changePassword.error?.response.text)
    }
  }, [changePassword.isSuccess, changePassword.isError])

  useEffect(() => {
    if (user) {
      history.push('/')
    }
  }, [user])

  if (!environment) {
    return null
  }
  const isChangePassword = screen === 'changePwd' && !!code

  const handleForgotPassword = () => {
    if (environment.serverSecurity === 'oAuth2') {
      window.location.href = `${getApiServer()}/auth/forgot-password`
    } else {
      handleOpenForgotLogin('Password')
    }
  }
  return (
    <section className='home'>
      <Container>
        <PageTitle displayName="Login" />
        <h1 className="visually-hidden">Login Page</h1>
        <Row className="justify-content-center">
          <Col md={6} className='left'>
            <div>
              {screen === 'changePwd' && code ? (
                <ChangePasswordForm
                  formHandler={changePasswordFormHandler}
                  alertState={changePassAlert}
                  closeAlert={handleCloseChangePassAlert}
                  isLoading={changePassword.isLoading}
                  validateForgotPasswordCode={validateForgotPasswordCode}
                  code={code}
                />
              ) : environment.serverSecurity !== 'oAuth2' ? (
                showMfaForm ? (
                  <EnterMfaForm
                    formHandler={mfaFormHandler}
                    alertState={mfaAlert}
                    closeAlert={handleCloseMfaAlert}
                    isLoading={validateMfaToken.isLoading || userIsLoading}
                  />
                ) : (
                  <LoginForm
                    formHandler={loginFormHandler}
                    alertState={loginAlert}
                    closeAlert={handleCloseLoginAlert}
                    openForgotModal={handleOpenForgotLogin}
                    isLoading={loginUser.isLoading}
                  />
                )
              ) : null}
            </div>
          </Col>

          {!isChangePassword && (
            <Col
              md={environment.serverSecurity === 'oAuth2' ? 7 : 6}
              className="right"
            >
              <section>
                {environment.serverSecurity === 'oAuth2' && (
                  <div>
                    {loginAlert.show && (
                      <Alert
                        variant={loginAlert.variant}
                        dismissible
                        onClose={handleCloseLoginAlert}
                        className="mt-3"
                      >
                        {loginAlert.message}
                      </Alert>
                    )}
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                      <Button
                        as="a"
                        href={`${getApiServer()}/oAuth2/start`}
                        variant="primary"
                        style={{ color: 'white' }}
                      >
                        Log In
                      </Button>

                      {!appConfig.use_sso && (
                        <span className="fs-5 fw-semibold">
                          or{' '}
                          <a href="/register" className="text-decoration-underline text-dark">
                            Register for an account
                          </a>
                        </span>
                      )}
                    </div>

                    <div className="d-flex gap-3 my-3">
                      <Button
                        variant="link"
                        className="p-0 text-decoration-underline text-dark"
                        onClick={() => handleOpenForgotLogin('Username')}
                      >
                        Forgot username?
                      </Button>

                      <Button
                        variant="link"
                        className="p-0 text-decoration-underline text-dark"
                        onClick={handleForgotPassword}
                      >
                        Forgot password?
                      </Button>
                    </div>

                    <hr className="my-3" />
                  </div>
                )}

                {appConfig.custom_login_content === 'healthspring' ? (
                  <HealthSpringLoginSection />
                ) : (
                  <>
                    <h3 className='h4'>
                      Online Access to your {companyDataDescription}
                    </h3>

                    <p>
                      Creating an account with the {preferedName} {dataPortalTitle} allows you to log in to the {preferedName} {dataPortalTitle} or use your account with approved external apps.
                    </p>

                    {!appConfig.use_sso && !appConfig.show_reregister_message && (
                      <p>
                        Want to create an account? <a href="/register">Register Now</a>
                      </p>
                    )}

                    {appConfig.show_reregister_message && (
                      <p>
                        <strong>IMPORTANT: All users must re-register.</strong>{' '}
                        If you had an account for the previous App Connect, upgrades to the
                        platform require everyone to set up a new account.{' '}
                        <Link to='/register/requestor'>Register Now</Link>
                      </p>
                    )}

                    <p>
                      Need more information? <a href="/help">Learn More</a>
                    </p>

                    {environment?.showDrugLookup && (
                      <p>
                        <strong>NEW</strong> Feature: <a href="/drug-lookup">Drug Lookup</a>
                      </p>
                    )}
                    {isDemoTenant(environment?.tenant || '') && (
                      <p className="mt-3 mb-0">
                        <strong>NEW</strong> - Data Sharing Opt In and Opt Out
                      </p>
                    )}
                  </>
                )}
              </section>
            </Col>
          )}
        </Row>

        <ForgotLoginModal
          show={showForgotLogin}
          onHide={handleHideForgotLogin}
          modalType={modalType}
          forgotLogin={forgotLogin}
        />

        <div className='lowerCallout'>
          <h4>
            {!appConfig.use_sso && (
              <Link to='/register/requestor'>
                New User? - Click here to Register
              </Link>
            )}
          </h4>
        </div>
      </Container>
    </section>
  )
}
export default Login